import { isChromeOverrides, isPigmentDocument } from "./document.js";
import { isBlockNode, isPigmentJsonValue } from "./model.js";
import { validatePigmentDocumentConformance, validatePigmentManifestExampleConformance } from "./conformance.js";
import { isThemeConfig } from "./theme.js";
/** The wire identifier for the portable, agent-discoverable Pigment contract. */
export const PIGMENT_CAPABILITY_MANIFEST_SCHEMA = "pigment.capability-manifest.v3";
/** The versioned, agent-consumable result contract for document validation. */
export const PIGMENT_DOCUMENT_VALIDATION_SCHEMA = "pigment.document-validation.v4";
const VALUE_KINDS = new Set([
    "string", "number", "boolean", "json", "color", "url", "richText", "id", "array", "object",
]);
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isNonEmptyText(value) {
    return typeof value === "string" && value.length > 0 && value === value.trim();
}
function issue(issues, path, code, message) {
    issues.push({ path, code, message });
}
function rejectUnknownKeys(record, allowed, path, issues) {
    for (const key of Object.keys(record)) {
        if (!allowed.includes(key))
            issue(issues, `${path}.${key}`, "unknown_key", "Unknown manifest key.");
    }
}
function validateJson(value, path, issues, ancestors = new Set()) {
    if (value === null || typeof value === "string" || typeof value === "boolean")
        return true;
    if (typeof value === "number") {
        if (Number.isFinite(value))
            return true;
        issue(issues, path, "invalid_value", "JSON numbers must be finite.");
        return false;
    }
    if (Array.isArray(value)) {
        if (ancestors.has(value)) {
            issue(issues, path, "invalid_value", "JSON values cannot be cyclic.");
            return false;
        }
        ancestors.add(value);
        const valid = value.every((item, index) => validateJson(item, `${path}[${index}]`, issues, ancestors));
        ancestors.delete(value);
        return valid;
    }
    if (isRecord(value)) {
        if (ancestors.has(value)) {
            issue(issues, path, "invalid_value", "JSON values cannot be cyclic.");
            return false;
        }
        ancestors.add(value);
        const valid = Object.entries(value).every(([key, item]) => validateJson(item, `${path}.${key}`, issues, ancestors));
        ancestors.delete(value);
        return valid;
    }
    issue(issues, path, "invalid_type", "Expected a JSON-serializable value.");
    return false;
}
function matchesValueKind(value, kind) {
    if (kind === "json")
        return true;
    if (kind === "number")
        return typeof value === "number";
    if (kind === "boolean")
        return typeof value === "boolean";
    if (kind === "array" || kind === "object")
        return false;
    return typeof value === "string";
}
function validateDeprecation(value, path, issues) {
    if (!isRecord(value)) {
        issue(issues, path, "invalid_type", "Expected a deprecation object.");
        return false;
    }
    rejectUnknownKeys(value, ["message", "replacement"], path, issues);
    if (!isNonEmptyText(value.message))
        issue(issues, `${path}.message`, "invalid_value", "Expected non-empty trimmed text.");
    if (value.replacement !== undefined && !isNonEmptyText(value.replacement)) {
        issue(issues, `${path}.replacement`, "invalid_value", "Expected non-empty trimmed text.");
    }
    return true;
}
function validateValueSchema(value, path, issues) {
    if (!isRecord(value)) {
        issue(issues, path, "invalid_type", "Expected a value schema object.");
        return false;
    }
    rejectUnknownKeys(value, ["kind", "description", "required", "format", "enum", "default", "items", "properties", "capabilityBinding", "resourceKind", "deprecated"], path, issues);
    const kind = value.kind;
    if (typeof kind !== "string" || !VALUE_KINDS.has(kind)) {
        issue(issues, `${path}.kind`, "invalid_value", "Expected a supported value kind.");
        return false;
    }
    if (value.description !== undefined && !isNonEmptyText(value.description))
        issue(issues, `${path}.description`, "invalid_value", "Expected non-empty trimmed text.");
    if (value.required !== undefined && typeof value.required !== "boolean")
        issue(issues, `${path}.required`, "invalid_type", "Expected a boolean.");
    if (value.format !== undefined && !isNonEmptyText(value.format))
        issue(issues, `${path}.format`, "invalid_value", "Expected non-empty trimmed text.");
    if (value.deprecated !== undefined)
        validateDeprecation(value.deprecated, `${path}.deprecated`, issues);
    if (value.capabilityBinding !== undefined) {
        if (value.capabilityBinding !== "action" && value.capabilityBinding !== "data") {
            issue(issues, `${path}.capabilityBinding`, "invalid_value", "Expected an action or data capability binding.");
        }
        else if (kind !== "object") {
            issue(issues, `${path}.capabilityBinding`, "incompatible_schema", "Capability bindings require an object schema.");
        }
    }
    if (value.resourceKind !== undefined) {
        if (value.resourceKind !== "link" && value.resourceKind !== "asset")
            issue(issues, `${path}.resourceKind`, "invalid_value", "Expected link or asset.");
        else if (kind !== "url")
            issue(issues, `${path}.resourceKind`, "incompatible_schema", "Resource semantics require a url schema.");
    }
    if (value.enum !== undefined) {
        if (!Array.isArray(value.enum) || value.enum.some((item) => item !== null && typeof item !== "string" && typeof item !== "number" && typeof item !== "boolean")) {
            issue(issues, `${path}.enum`, "invalid_type", "Expected an array of JSON primitive values.");
        }
        else if (value.enum.some((item) => typeof item === "number" && !Number.isFinite(item))) {
            issue(issues, `${path}.enum`, "invalid_value", "Numeric enum values must be finite.");
        }
        else if (value.enum.some((item) => !matchesValueKind(item, kind))) {
            issue(issues, `${path}.enum`, "incompatible_schema", "Enum values must match the declared value kind.");
        }
    }
    if (value.default !== undefined) {
        const defaultValue = value.default;
        const validJson = validateJson(defaultValue, `${path}.default`, issues);
        if (validJson && (kind === "array" ? !Array.isArray(defaultValue) : kind === "object" ? !isRecord(defaultValue) : !Array.isArray(defaultValue) && !isRecord(defaultValue) && !matchesValueKind(defaultValue, kind))) {
            issue(issues, `${path}.default`, "incompatible_schema", "Default value must match the declared value kind.");
        }
    }
    if (value.items !== undefined) {
        if (kind !== "array")
            issue(issues, `${path}.items`, "incompatible_schema", "Only array schemas may declare items.");
        validateValueSchema(value.items, `${path}.items`, issues);
    }
    if (value.properties !== undefined) {
        if (kind !== "object")
            issue(issues, `${path}.properties`, "incompatible_schema", "Only object schemas may declare properties.");
        if (!isRecord(value.properties))
            issue(issues, `${path}.properties`, "invalid_type", "Expected a property-schema record.");
        else
            for (const [key, child] of Object.entries(value.properties)) {
                if (!isNonEmptyText(key))
                    issue(issues, `${path}.properties.${key}`, "invalid_value", "Property names must be non-empty trimmed text.");
                validateValueSchema(child, `${path}.properties.${key}`, issues);
            }
    }
    return true;
}
function validateSchemaRecord(value, path, issues) {
    if (!isRecord(value)) {
        issue(issues, path, "invalid_type", "Expected a property-schema record.");
        return false;
    }
    for (const [key, schema] of Object.entries(value)) {
        if (!isNonEmptyText(key))
            issue(issues, `${path}.${key}`, "invalid_value", "Property names must be non-empty trimmed text.");
        validateValueSchema(schema, `${path}.${key}`, issues);
    }
    return true;
}
function validateStringList(value, path, issues) {
    if (!Array.isArray(value)) {
        issue(issues, path, "invalid_type", "Expected an array of names.");
        return false;
    }
    const names = new Set();
    for (const [index, name] of value.entries()) {
        if (!isNonEmptyText(name))
            issue(issues, `${path}[${index}]`, "invalid_value", "Names must be non-empty trimmed text.");
        else if (names.has(name))
            issue(issues, `${path}[${index}]`, "duplicate", `Duplicate name "${name}".`);
        else
            names.add(name);
    }
    return true;
}
function validateResourcePolicy(value, path, issues) {
    if (!isRecord(value)) {
        issue(issues, path, "invalid_type", "Expected a resource policy object.");
        return false;
    }
    rejectUnknownKeys(value, ["allowedSchemes", "allowRelative", "allowedOrigins"], path, issues);
    if (validateStringList(value.allowedSchemes, `${path}.allowedSchemes`, issues)) {
        for (const [index, scheme] of value.allowedSchemes.entries()) {
            if (!/^[a-z][a-z0-9+.-]*$/u.test(scheme)) {
                issue(issues, `${path}.allowedSchemes[${index}]`, "invalid_value", "Schemes must be lowercase names without a colon.");
            }
        }
    }
    if (value.allowRelative !== undefined && typeof value.allowRelative !== "boolean") {
        issue(issues, `${path}.allowRelative`, "invalid_type", "Expected a boolean.");
    }
    if (value.allowedOrigins !== undefined && validateStringList(value.allowedOrigins, `${path}.allowedOrigins`, issues)) {
        for (const [index, origin] of value.allowedOrigins.entries()) {
            try {
                const parsed = new URL(origin);
                if (parsed.origin !== origin || (parsed.protocol !== "https:" && parsed.protocol !== "http:"))
                    throw new Error("invalid origin");
            }
            catch {
                issue(issues, `${path}.allowedOrigins[${index}]`, "invalid_value", "Expected an exact http(s) origin without a path.");
            }
        }
    }
    return true;
}
function validateAssetResourcePolicy(value, path, issues) {
    if (!isRecord(value)) {
        issue(issues, path, "invalid_type", "Expected an asset resource policy object.");
        return false;
    }
    // Validate the shared fields without making requireMetadata look like an
    // unknown resource-policy key.
    const shared = Object.fromEntries(Object.entries(value).filter(([key]) => key !== "requireMetadata"));
    validateResourcePolicy(shared, path, issues);
    if (value.requireMetadata !== undefined && typeof value.requireMetadata !== "boolean") {
        issue(issues, `${path}.requireMetadata`, "invalid_type", "Expected a boolean.");
    }
    return true;
}
function validateAuthoredAssetBudget(value, path, issues) {
    if (!isRecord(value)) {
        issue(issues, path, "invalid_type", "Expected an authored-asset budget object.");
        return false;
    }
    const keys = ["maxUniqueReferences", "maxSingleAssetBytes", "maxUniqueTransferBytes"];
    rejectUnknownKeys(value, keys, path, issues);
    if (!keys.some((key) => value[key] !== undefined)) {
        issue(issues, path, "invalid_value", "An authored-asset budget must declare at least one limit.");
    }
    for (const key of keys) {
        const limit = value[key];
        if (limit !== undefined && (!Number.isSafeInteger(limit) || limit < 0)) {
            issue(issues, `${path}.${key}`, "invalid_value", "Expected a non-negative safe integer.");
        }
    }
    return true;
}
function validateValidationPolicy(value, path, issues) {
    if (!isRecord(value)) {
        issue(issues, path, "invalid_type", "Expected a validation policy object.");
        return false;
    }
    rejectUnknownKeys(value, ["links", "assets", "authoredAssetBudget"], path, issues);
    if (value.links !== undefined)
        validateResourcePolicy(value.links, `${path}.links`, issues);
    if (value.assets !== undefined)
        validateAssetResourcePolicy(value.assets, `${path}.assets`, issues);
    if (value.authoredAssetBudget !== undefined)
        validateAuthoredAssetBudget(value.authoredAssetBudget, `${path}.authoredAssetBudget`, issues);
    return true;
}
function validateAssets(value, issues) {
    if (!Array.isArray(value)) {
        issue(issues, "$.assets", "invalid_type", "Expected an array of authored-asset metadata.");
        return false;
    }
    const references = new Set();
    for (const [index, asset] of value.entries()) {
        const path = `$.assets[${index}]`;
        if (!isRecord(asset)) {
            issue(issues, path, "invalid_type", "Expected an authored-asset metadata object.");
            continue;
        }
        rejectUnknownKeys(asset, ["reference", "mediaType", "byteSize", "sha256"], path, issues);
        if (!isNonEmptyText(asset.reference))
            issue(issues, `${path}.reference`, "invalid_value", "Expected a non-empty trimmed reference.");
        else if (references.has(asset.reference))
            issue(issues, `${path}.reference`, "duplicate", `Duplicate asset reference "${asset.reference}".`);
        else
            references.add(asset.reference);
        if (!isNonEmptyText(asset.mediaType) || !/^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/u.test(asset.mediaType)) {
            issue(issues, `${path}.mediaType`, "invalid_value", "Expected a canonical lowercase MIME media type.");
        }
        if (!Number.isSafeInteger(asset.byteSize) || asset.byteSize < 0) {
            issue(issues, `${path}.byteSize`, "invalid_value", "Expected a non-negative safe integer byte size.");
        }
        if (typeof asset.sha256 !== "string" || !/^[0-9a-f]{64}$/u.test(asset.sha256)) {
            issue(issues, `${path}.sha256`, "invalid_value", "Expected a lowercase 64-character SHA-256 digest.");
        }
    }
    return true;
}
function validateAssetGovernance(assets, validation, issues) {
    if (!isRecord(validation))
        return;
    const assetPolicy = isRecord(validation.assets) ? validation.assets : undefined;
    const requireMetadata = assetPolicy?.requireMetadata === true;
    const hasInventory = Array.isArray(assets) && assets.length > 0;
    if (requireMetadata && !hasInventory) {
        issue(issues, "$.validation.assets.requireMetadata", "incompatible_schema", "Metadata enforcement requires a non-empty authored-asset inventory.");
    }
    if (validation.authoredAssetBudget !== undefined && !requireMetadata) {
        issue(issues, "$.validation.authoredAssetBudget", "incompatible_schema", "Authored-asset budgets require validation.assets.requireMetadata to be true.");
    }
}
function validateResourceBindings(value, path, props, issues) {
    if (!Array.isArray(value)) {
        issue(issues, path, "invalid_type", "Expected an array of resource bindings.");
        return false;
    }
    const properties = isRecord(props) ? props : {};
    const names = new Set();
    for (const [index, binding] of value.entries()) {
        const bindingPath = `${path}[${index}]`;
        if (!isRecord(binding)) {
            issue(issues, bindingPath, "invalid_type", "Expected a resource binding object.");
            continue;
        }
        rejectUnknownKeys(binding, ["property", "kind"], bindingPath, issues);
        if (!isNonEmptyText(binding.property))
            issue(issues, `${bindingPath}.property`, "invalid_value", "Expected non-empty trimmed text.");
        else if (names.has(binding.property))
            issue(issues, `${bindingPath}.property`, "duplicate", `Duplicate resource binding for "${binding.property}".`);
        else {
            names.add(binding.property);
            const schema = properties[binding.property];
            if (!isRecord(schema) || schema.kind !== "url") {
                issue(issues, `${bindingPath}.property`, "incompatible_schema", "Resource bindings must reference a declared url prop.");
            }
        }
        if (binding.kind !== "link" && binding.kind !== "asset")
            issue(issues, `${bindingPath}.kind`, "invalid_value", "Expected link or asset.");
    }
    return true;
}
function validateDataBindings(value, path, props, issues) {
    if (!Array.isArray(value)) {
        issue(issues, path, "invalid_type", "Expected an array of data bindings.");
        return false;
    }
    const properties = isRecord(props) ? props : {};
    const names = new Set();
    for (const [index, binding] of value.entries()) {
        const bindingPath = `${path}[${index}]`;
        if (!isRecord(binding)) {
            issue(issues, bindingPath, "invalid_type", "Expected a data binding object.");
            continue;
        }
        rejectUnknownKeys(binding, ["property", "mutuallyExclusiveWith"], bindingPath, issues);
        if (!isNonEmptyText(binding.property)) {
            issue(issues, `${bindingPath}.property`, "invalid_value", "Expected non-empty trimmed text.");
        }
        else if (names.has(binding.property)) {
            issue(issues, `${bindingPath}.property`, "duplicate", `Duplicate data binding for "${binding.property}".`);
        }
        else {
            names.add(binding.property);
            const schema = properties[binding.property];
            if (!isRecord(schema) || schema.kind !== "object" || schema.capabilityBinding !== "data") {
                issue(issues, `${bindingPath}.property`, "incompatible_schema", "Data bindings must reference a declared object prop with capabilityBinding data.");
            }
        }
        if (binding.mutuallyExclusiveWith !== undefined && validateStringList(binding.mutuallyExclusiveWith, `${bindingPath}.mutuallyExclusiveWith`, issues)) {
            for (const [exclusiveIndex, property] of binding.mutuallyExclusiveWith.entries()) {
                if (!Object.hasOwn(properties, property)) {
                    issue(issues, `${bindingPath}.mutuallyExclusiveWith[${exclusiveIndex}]`, "incompatible_schema", `Unknown mutually exclusive prop "${property}".`);
                }
                if (property === binding.property) {
                    issue(issues, `${bindingPath}.mutuallyExclusiveWith[${exclusiveIndex}]`, "incompatible_schema", "A data binding cannot conflict with itself.");
                }
            }
        }
    }
    return true;
}
function validateAccessibilityPolicy(value, path, props, issues) {
    if (!isRecord(value)) {
        issue(issues, path, "invalid_type", "Expected an accessibility policy object.");
        return false;
    }
    rejectUnknownKeys(value, ["imageAlternative"], path, issues);
    if (value.imageAlternative === undefined)
        return true;
    const imagePath = `${path}.imageAlternative`;
    if (!isRecord(value.imageAlternative)) {
        issue(issues, imagePath, "invalid_type", "Expected an image-alternative policy object.");
        return true;
    }
    const image = value.imageAlternative;
    rejectUnknownKeys(image, ["sourceProperty", "alternativeProperty", "allowEmptyAlternative"], imagePath, issues);
    const properties = isRecord(props) ? props : {};
    for (const key of ["sourceProperty", "alternativeProperty"]) {
        if (!isNonEmptyText(image[key]))
            issue(issues, `${imagePath}.${key}`, "invalid_value", "Expected non-empty trimmed text.");
        else if (!isRecord(properties[image[key]]))
            issue(issues, `${imagePath}.${key}`, "incompatible_schema", "Policy must reference a declared prop.");
    }
    const sourceSchema = isNonEmptyText(image.sourceProperty) ? properties[image.sourceProperty] : undefined;
    const alternativeSchema = isNonEmptyText(image.alternativeProperty) ? properties[image.alternativeProperty] : undefined;
    if (isRecord(sourceSchema) && sourceSchema.kind !== "url") {
        issue(issues, `${imagePath}.sourceProperty`, "incompatible_schema", "Image sources must reference a url prop.");
    }
    if (isRecord(alternativeSchema) && !["string", "richText"].includes(String(alternativeSchema.kind))) {
        issue(issues, `${imagePath}.alternativeProperty`, "incompatible_schema", "Image alternatives must reference a string or richText prop.");
    }
    if (image.sourceProperty === image.alternativeProperty)
        issue(issues, imagePath, "incompatible_schema", "Source and alternative properties must differ.");
    if (image.allowEmptyAlternative !== undefined && typeof image.allowEmptyAlternative !== "boolean") {
        issue(issues, `${imagePath}.allowEmptyAlternative`, "invalid_type", "Expected a boolean.");
    }
    return true;
}
function validateResponsivePolicy(value, path, props, issues) {
    if (!isRecord(value)) {
        issue(issues, path, "invalid_type", "Expected a responsive policy object.");
        return false;
    }
    rejectUnknownKeys(value, ["layout", "columnsProperty", "minColumnWidthProperty", "minColumnWidthPx", "maxColumnWidthPx", "allowedExplicitTemplates"], path, issues);
    if (value.layout !== "intrinsic-grid")
        issue(issues, `${path}.layout`, "invalid_value", "Expected intrinsic-grid.");
    const properties = isRecord(props) ? props : {};
    for (const key of ["columnsProperty", "minColumnWidthProperty"]) {
        if (!isNonEmptyText(value[key]))
            issue(issues, `${path}.${key}`, "invalid_value", "Expected non-empty trimmed text.");
        else {
            const schema = properties[value[key]];
            if (!isRecord(schema) || (schema.kind !== "string" && schema.kind !== "richText")) {
                issue(issues, `${path}.${key}`, "incompatible_schema", "Responsive policies must reference a declared string prop.");
            }
        }
    }
    for (const key of ["minColumnWidthPx", "maxColumnWidthPx"]) {
        if (typeof value[key] !== "number" || !Number.isFinite(value[key]) || value[key] <= 0) {
            issue(issues, `${path}.${key}`, "invalid_value", "Expected a positive finite pixel measure.");
        }
    }
    if (typeof value.minColumnWidthPx === "number" && typeof value.maxColumnWidthPx === "number" && value.minColumnWidthPx > value.maxColumnWidthPx) {
        issue(issues, path, "incompatible_schema", "minColumnWidthPx cannot exceed maxColumnWidthPx.");
    }
    if (value.allowedExplicitTemplates !== undefined)
        validateStringList(value.allowedExplicitTemplates, `${path}.allowedExplicitTemplates`, issues);
    return true;
}
function validateBlockSlot(value, path, issues) {
    if (!isRecord(value)) {
        issue(issues, path, "invalid_type", "Expected a block-slot object.");
        return false;
    }
    rejectUnknownKeys(value, ["name", "description", "minChildren", "maxChildren", "allowedTypes"], path, issues);
    if (!isNonEmptyText(value.name))
        issue(issues, `${path}.name`, "invalid_value", "Expected non-empty trimmed text.");
    if (value.description !== undefined && !isNonEmptyText(value.description))
        issue(issues, `${path}.description`, "invalid_value", "Expected non-empty trimmed text.");
    for (const key of ["minChildren", "maxChildren"]) {
        if (value[key] !== undefined && (!Number.isInteger(value[key]) || value[key] < 0)) {
            issue(issues, `${path}.${key}`, "invalid_value", "Expected a non-negative integer.");
        }
    }
    if (typeof value.minChildren === "number" && typeof value.maxChildren === "number" && value.minChildren > value.maxChildren) {
        issue(issues, path, "incompatible_schema", "minChildren cannot exceed maxChildren.");
    }
    if (value.allowedTypes !== undefined)
        validateStringList(value.allowedTypes, `${path}.allowedTypes`, issues);
    return true;
}
function validateBlocks(value, issues) {
    if (!Array.isArray(value)) {
        issue(issues, "blocks", "invalid_type", "Expected an array of block capabilities.");
        return false;
    }
    const names = new Set();
    for (const [index, block] of value.entries()) {
        const path = `blocks[${index}]`;
        if (!isRecord(block)) {
            issue(issues, path, "invalid_type", "Expected a block capability object.");
            continue;
        }
        rejectUnknownKeys(block, ["type", "label", "description", "props", "allowedChildren", "slots", "dataCapabilities", "dataBindings", "actionCapabilities", "resourceBindings", "accessibility", "responsive", "examples", "deprecated"], path, issues);
        if (!isNonEmptyText(block.type))
            issue(issues, `${path}.type`, "invalid_value", "Expected non-empty trimmed text.");
        else if (names.has(block.type))
            issue(issues, `${path}.type`, "duplicate", `Duplicate block type "${block.type}".`);
        else
            names.add(block.type);
        if (block.label !== undefined && !isNonEmptyText(block.label))
            issue(issues, `${path}.label`, "invalid_value", "Expected non-empty trimmed text.");
        if (block.description !== undefined && !isNonEmptyText(block.description))
            issue(issues, `${path}.description`, "invalid_value", "Expected non-empty trimmed text.");
        if (block.props !== undefined)
            validateSchemaRecord(block.props, `${path}.props`, issues);
        if (block.allowedChildren !== undefined)
            validateStringList(block.allowedChildren, `${path}.allowedChildren`, issues);
        if (block.dataCapabilities !== undefined)
            validateStringList(block.dataCapabilities, `${path}.dataCapabilities`, issues);
        if (block.dataBindings !== undefined)
            validateDataBindings(block.dataBindings, `${path}.dataBindings`, block.props, issues);
        if (block.actionCapabilities !== undefined)
            validateStringList(block.actionCapabilities, `${path}.actionCapabilities`, issues);
        if (block.resourceBindings !== undefined)
            validateResourceBindings(block.resourceBindings, `${path}.resourceBindings`, block.props, issues);
        if (block.accessibility !== undefined)
            validateAccessibilityPolicy(block.accessibility, `${path}.accessibility`, block.props, issues);
        if (block.responsive !== undefined)
            validateResponsivePolicy(block.responsive, `${path}.responsive`, block.props, issues);
        if (block.slots !== undefined) {
            if (!Array.isArray(block.slots))
                issue(issues, `${path}.slots`, "invalid_type", "Expected an array of block slots.");
            else {
                const slots = new Set();
                for (const [slotIndex, slot] of block.slots.entries()) {
                    validateBlockSlot(slot, `${path}.slots[${slotIndex}]`, issues);
                    if (isRecord(slot) && isNonEmptyText(slot.name)) {
                        if (slots.has(slot.name))
                            issue(issues, `${path}.slots[${slotIndex}].name`, "duplicate", `Duplicate slot "${slot.name}".`);
                        else
                            slots.add(slot.name);
                    }
                }
            }
        }
        if (block.examples !== undefined) {
            if (!Array.isArray(block.examples))
                issue(issues, `${path}.examples`, "invalid_type", "Expected an array of block nodes.");
            else
                block.examples.forEach((example, exampleIndex) => {
                    if (!isBlockNode(example))
                        issue(issues, `${path}.examples[${exampleIndex}]`, "invalid_value", "Expected a valid block node.");
                    validateJson(example, `${path}.examples[${exampleIndex}]`, issues);
                });
        }
        if (block.deprecated !== undefined)
            validateDeprecation(block.deprecated, `${path}.deprecated`, issues);
    }
    return true;
}
function validateBlockReferences(value, issues) {
    if (!Array.isArray(value))
        return;
    const declaredTypes = new Set();
    for (const block of value) {
        if (isRecord(block) && isNonEmptyText(block.type))
            declaredTypes.add(block.type);
    }
    for (const [index, block] of value.entries()) {
        if (!isRecord(block))
            continue;
        if (Array.isArray(block.allowedChildren)) {
            for (const [childIndex, type] of block.allowedChildren.entries()) {
                if (isNonEmptyText(type) && !declaredTypes.has(type)) {
                    issue(issues, `blocks[${index}].allowedChildren[${childIndex}]`, "incompatible_schema", `Unknown declared block type "${type}".`);
                }
            }
        }
        if (!Array.isArray(block.slots))
            continue;
        for (const [slotIndex, slot] of block.slots.entries()) {
            if (!isRecord(slot) || !Array.isArray(slot.allowedTypes))
                continue;
            for (const [typeIndex, type] of slot.allowedTypes.entries()) {
                if (isNonEmptyText(type) && !declaredTypes.has(type)) {
                    issue(issues, `blocks[${index}].slots[${slotIndex}].allowedTypes[${typeIndex}]`, "incompatible_schema", `Unknown declared block type "${type}".`);
                }
            }
        }
    }
}
function validateBlockCapabilityReferences(value, globalCapabilities, blockKey, issues) {
    if (!Array.isArray(value))
        return;
    const declaredNames = new Set();
    if (Array.isArray(globalCapabilities)) {
        for (const capability of globalCapabilities) {
            if (isRecord(capability) && isNonEmptyText(capability.name))
                declaredNames.add(capability.name);
        }
    }
    for (const [blockIndex, block] of value.entries()) {
        if (!isRecord(block) || !Array.isArray(block[blockKey]))
            continue;
        for (const [capabilityIndex, name] of block[blockKey].entries()) {
            if (isNonEmptyText(name) && !declaredNames.has(name)) {
                issue(issues, `blocks[${blockIndex}].${blockKey}[${capabilityIndex}]`, "incompatible_schema", `Unknown declared ${blockKey === "dataCapabilities" ? "data" : "action"} capability "${name}".`);
            }
        }
    }
}
function validateResourceBindingPolicies(value, validation, issues) {
    if (!Array.isArray(value))
        return;
    const policies = isRecord(validation) ? validation : {};
    for (const [blockIndex, block] of value.entries()) {
        if (!isRecord(block) || !Array.isArray(block.resourceBindings))
            continue;
        for (const [bindingIndex, binding] of block.resourceBindings.entries()) {
            if (!isRecord(binding))
                continue;
            const policyKey = binding.kind === "link" ? "links" : binding.kind === "asset" ? "assets" : null;
            if (policyKey && !isRecord(policies[policyKey])) {
                issue(issues, `blocks[${blockIndex}].resourceBindings[${bindingIndex}].kind`, "incompatible_schema", `Resource binding requires validation.${policyKey}.`);
            }
        }
    }
}
function validateSchemaSemanticPolicies(value, path, validation, issues) {
    if (!isRecord(value))
        return;
    if (value.resourceKind === "link" && (!isRecord(validation) || !isRecord(validation.links))) {
        issue(issues, `${path}.resourceKind`, "incompatible_schema", "Nested link semantics require validation.links.");
    }
    if (value.resourceKind === "asset" && (!isRecord(validation) || !isRecord(validation.assets))) {
        issue(issues, `${path}.resourceKind`, "incompatible_schema", "Nested asset semantics require validation.assets.");
    }
    if (isRecord(value.items))
        validateSchemaSemanticPolicies(value.items, `${path}.items`, validation, issues);
    if (isRecord(value.properties)) {
        for (const [key, schema] of Object.entries(value.properties)) {
            validateSchemaSemanticPolicies(schema, `${path}.properties.${key}`, validation, issues);
        }
    }
}
function validateNestedSchemaSemanticPolicies(value, issues) {
    const validation = value.validation;
    if (Array.isArray(value.blocks)) {
        for (const [blockIndex, block] of value.blocks.entries()) {
            if (!isRecord(block) || !isRecord(block.props))
                continue;
            for (const [key, schema] of Object.entries(block.props)) {
                validateSchemaSemanticPolicies(schema, `blocks[${blockIndex}].props.${key}`, validation, issues);
            }
        }
    }
    for (const capabilityKey of ["dataCapabilities", "actionCapabilities"]) {
        const capabilities = value[capabilityKey];
        if (!Array.isArray(capabilities))
            continue;
        for (const [capabilityIndex, capability] of capabilities.entries()) {
            if (!isRecord(capability))
                continue;
            if (isRecord(capability.input)) {
                for (const [key, schema] of Object.entries(capability.input)) {
                    validateSchemaSemanticPolicies(schema, `${capabilityKey}[${capabilityIndex}].input.${key}`, validation, issues);
                }
            }
            if (capabilityKey === "dataCapabilities" && isRecord(capability.output)) {
                validateSchemaSemanticPolicies(capability.output, `${capabilityKey}[${capabilityIndex}].output`, validation, issues);
            }
        }
    }
}
function validateNamedCapabilities(value, path, issues) {
    if (!Array.isArray(value)) {
        issue(issues, path, "invalid_type", "Expected an array of named capabilities.");
        return false;
    }
    const names = new Set();
    for (const [index, item] of value.entries()) {
        const itemPath = `${path}[${index}]`;
        if (!isRecord(item)) {
            issue(issues, itemPath, "invalid_type", "Expected a capability object.");
            continue;
        }
        const token = path === "tokens";
        rejectUnknownKeys(item, token ? ["name", "kind", "description", "deprecated"] : ["name", "description", "input", "output", "deprecated"], itemPath, issues);
        if (!isNonEmptyText(item.name))
            issue(issues, `${itemPath}.name`, "invalid_value", "Expected non-empty trimmed text.");
        else if (names.has(item.name))
            issue(issues, `${itemPath}.name`, "duplicate", `Duplicate capability "${item.name}".`);
        else
            names.add(item.name);
        if (item.description !== undefined && !isNonEmptyText(item.description))
            issue(issues, `${itemPath}.description`, "invalid_value", "Expected non-empty trimmed text.");
        if (token && (typeof item.kind !== "string" || !VALUE_KINDS.has(item.kind)))
            issue(issues, `${itemPath}.kind`, "invalid_value", "Expected a supported value kind.");
        if (!token && item.input !== undefined)
            validateSchemaRecord(item.input, `${itemPath}.input`, issues);
        if (!token && item.output !== undefined)
            validateValueSchema(item.output, `${itemPath}.output`, issues);
        if (item.deprecated !== undefined)
            validateDeprecation(item.deprecated, `${itemPath}.deprecated`, issues);
    }
    return true;
}
function validateExamples(value, issues) {
    if (!Array.isArray(value)) {
        issue(issues, "examples", "invalid_type", "Expected an array of manifest examples.");
        return false;
    }
    const ids = new Set();
    for (const [index, example] of value.entries()) {
        const path = `examples[${index}]`;
        if (!isRecord(example)) {
            issue(issues, path, "invalid_type", "Expected a manifest example object.");
            continue;
        }
        rejectUnknownKeys(example, ["id", "title", "description", "document"], path, issues);
        if (!isNonEmptyText(example.id))
            issue(issues, `${path}.id`, "invalid_value", "Expected non-empty trimmed text.");
        else if (ids.has(example.id))
            issue(issues, `${path}.id`, "duplicate", `Duplicate example id "${example.id}".`);
        else
            ids.add(example.id);
        if (example.title !== undefined && !isNonEmptyText(example.title))
            issue(issues, `${path}.title`, "invalid_value", "Expected non-empty trimmed text.");
        if (example.description !== undefined && !isNonEmptyText(example.description))
            issue(issues, `${path}.description`, "invalid_value", "Expected non-empty trimmed text.");
        if (!isPigmentDocument(example.document))
            issue(issues, `${path}.document`, "invalid_value", "Expected a valid Pigment document.");
        else
            validateJson(example.document, `${path}.document`, issues);
    }
    return true;
}
function appendManifestExampleConformanceIssues(manifest, issues) {
    for (const finding of validatePigmentManifestExampleConformance(manifest)) {
        if (finding.severity === "warning")
            continue;
        issue(issues, finding.path, finding.code === "invalid_block_id" || finding.code === "invalid_slot" ? "invalid_value"
            : finding.code === "duplicate_block_id" ? "duplicate"
                : "incompatible_schema", finding.message);
    }
}
/** Validate an untrusted manifest and, on success, return its canonical order. */
export function validatePigmentCapabilityManifest(value) {
    const issues = [];
    if (!isRecord(value)) {
        return { ok: false, issues: [{ path: "$", code: "invalid_type", message: "Expected a capability manifest object." }] };
    }
    rejectUnknownKeys(value, ["schema", "version", "blocks", "tokens", "dataCapabilities", "actionCapabilities", "assets", "validation", "examples"], "$", issues);
    if (value.schema !== PIGMENT_CAPABILITY_MANIFEST_SCHEMA)
        issue(issues, "$.schema", "invalid_value", `Expected "${PIGMENT_CAPABILITY_MANIFEST_SCHEMA}".`);
    if (!isNonEmptyText(value.version))
        issue(issues, "$.version", "invalid_value", "Expected non-empty trimmed text.");
    validateBlocks(value.blocks, issues);
    validateBlockReferences(value.blocks, issues);
    if (value.tokens !== undefined)
        validateNamedCapabilities(value.tokens, "tokens", issues);
    if (value.dataCapabilities !== undefined)
        validateNamedCapabilities(value.dataCapabilities, "dataCapabilities", issues);
    if (value.actionCapabilities !== undefined)
        validateNamedCapabilities(value.actionCapabilities, "actionCapabilities", issues);
    if (value.assets !== undefined)
        validateAssets(value.assets, issues);
    if (value.validation !== undefined)
        validateValidationPolicy(value.validation, "$.validation", issues);
    validateBlockCapabilityReferences(value.blocks, value.dataCapabilities, "dataCapabilities", issues);
    validateBlockCapabilityReferences(value.blocks, value.actionCapabilities, "actionCapabilities", issues);
    validateResourceBindingPolicies(value.blocks, value.validation, issues);
    validateNestedSchemaSemanticPolicies(value, issues);
    validateAssetGovernance(value.assets, value.validation, issues);
    if (value.examples !== undefined)
        validateExamples(value.examples, issues);
    // Example conformance deliberately shares the authored-document traversal.
    // It only runs once the manifest shape is sound, so the traversal receives a
    // trustworthy capability inventory and can stay fail-closed without guards.
    if (issues.length === 0)
        appendManifestExampleConformanceIssues(value, issues);
    if (issues.length > 0)
        return { ok: false, issues };
    return { ok: true, manifest: normalizePigmentCapabilityManifest(value), issues: [] };
}
function diagnostic(diagnostics, entry) {
    diagnostics.push({
        ...entry,
        repairKind: entry.repairKind ?? (entry.severity === "warning" ? "replace_deprecated_block" : "repair_document_shape"),
    });
}
function isPortableRecord(value) {
    if (!isRecord(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function validatePortableBlockShape(value, path, diagnostics, ancestors) {
    if (!isPortableRecord(value)) {
        diagnostic(diagnostics, { code: "malformed_document", severity: "error", path, message: "Expected a portable block object.", repairKind: "repair_document_shape" });
        return;
    }
    if (ancestors.has(value)) {
        diagnostic(diagnostics, { code: "nonportable_document", severity: "error", path, message: "Block trees cannot be cyclic.", repairKind: "replace_nonportable_value" });
        return;
    }
    ancestors.add(value);
    for (const key of Object.keys(value)) {
        if (!["id", "type", "slot", "props", "children"].includes(key)) {
            diagnostic(diagnostics, { code: "malformed_document", severity: "error", path: `${path}.${key}`, message: "Unknown block key.", repairKind: "repair_document_shape" });
        }
    }
    if (typeof value.id !== "string")
        diagnostic(diagnostics, { code: "malformed_document", severity: "error", path: `${path}.id`, message: "Block id must be a string.", repairKind: "repair_document_shape" });
    if (typeof value.type !== "string")
        diagnostic(diagnostics, { code: "malformed_document", severity: "error", path: `${path}.type`, message: "Block type must be a string.", repairKind: "repair_document_shape" });
    if (value.slot !== undefined && typeof value.slot !== "string")
        diagnostic(diagnostics, { code: "malformed_document", severity: "error", path: `${path}.slot`, message: "Block slot must be a string.", repairKind: "repair_document_shape" });
    if (value.props !== undefined) {
        if (!isPortableRecord(value.props))
            diagnostic(diagnostics, { code: "nonportable_document", severity: "error", path: `${path}.props`, message: "Block props must be a plain JSON object.", repairKind: "replace_nonportable_value" });
        else if (!isPigmentJsonValue(value.props))
            diagnostic(diagnostics, { code: "nonportable_document", severity: "error", path: `${path}.props`, message: "Block props must be JSON-serializable.", repairKind: "replace_nonportable_value" });
    }
    if (value.children !== undefined) {
        if (!Array.isArray(value.children))
            diagnostic(diagnostics, { code: "malformed_document", severity: "error", path: `${path}.children`, message: "Block children must be an array.", repairKind: "repair_document_shape" });
        else
            value.children.forEach((child, index) => validatePortableBlockShape(child, `${path}.children[${index}]`, diagnostics, ancestors));
    }
    ancestors.delete(value);
}
function validateDocumentShape(value) {
    const diagnostics = [];
    if (!isPortableRecord(value)) {
        diagnostic(diagnostics, { code: "malformed_document", severity: "error", path: "$", message: "Expected a portable Pigment document object.", repairKind: "repair_document_shape" });
        return diagnostics;
    }
    for (const key of Object.keys(value)) {
        if (!["theme", "blocks", "chrome"].includes(key))
            diagnostic(diagnostics, { code: "malformed_document", severity: "error", path: `$.${key}`, message: "Unknown document key.", repairKind: "repair_document_shape" });
    }
    if (value.theme !== undefined) {
        if (!isThemeConfig(value.theme))
            diagnostic(diagnostics, { code: "malformed_document", severity: "error", path: "$.theme", message: "Theme does not match the Pigment theme contract.", repairKind: "repair_document_shape" });
        else if (!isPigmentJsonValue(value.theme))
            diagnostic(diagnostics, { code: "nonportable_document", severity: "error", path: "$.theme", message: "Theme values must be JSON-serializable.", repairKind: "replace_nonportable_value" });
    }
    if (value.blocks !== undefined) {
        if (!Array.isArray(value.blocks))
            diagnostic(diagnostics, { code: "malformed_document", severity: "error", path: "$.blocks", message: "Document blocks must be an array.", repairKind: "repair_document_shape" });
        else
            value.blocks.forEach((block, index) => validatePortableBlockShape(block, `$.blocks[${index}]`, diagnostics, new Set()));
    }
    if (!isChromeOverrides(value.chrome)) {
        diagnostic(diagnostics, { code: "malformed_document", severity: "error", path: "$.chrome", message: "Chrome overrides do not match the Pigment document contract.", repairKind: "repair_document_shape" });
    }
    else if (value.chrome !== undefined && value.chrome !== null && !isPigmentJsonValue(value.chrome)) {
        diagnostic(diagnostics, { code: "nonportable_document", severity: "error", path: "$.chrome", message: "Chrome overrides must be a plain JSON object.", repairKind: "replace_nonportable_value" });
    }
    else if (isPortableRecord(value.chrome)) {
        for (const slot of ["announcement", "header", "footer"]) {
            const override = value.chrome[slot];
            if (override?.blocks)
                override.blocks.forEach((block, index) => validatePortableBlockShape(block, `$.chrome.${slot}.blocks[${index}]`, diagnostics, new Set()));
        }
    }
    return diagnostics;
}
function repairKindForConformance(issue) {
    switch (issue.code) {
        case "invalid_block_id": return "assign_block_id";
        case "duplicate_block_id": return "deduplicate_block_id";
        case "unknown_block_type": return "declare_or_replace_block_type";
        case "root_slot_not_allowed": return "remove_root_slot";
        case "invalid_slot":
        case "unknown_slot": return "assign_declared_slot";
        case "disallowed_child": return "move_or_replace_child";
        case "required_prop_missing": return "add_required_prop";
        case "unknown_prop": return "remove_or_declare_prop";
        case "invalid_prop": return "fix_prop_value";
        case "slot_min_children":
        case "slot_max_children": return "adjust_slot_children";
        case "deprecated_block": return "replace_deprecated_block";
        case "deprecated_prop": return "replace_deprecated_prop";
        case "disallowed_link_scheme":
        case "disallowed_link_origin":
        case "disallowed_asset_scheme":
        case "disallowed_asset_origin":
        case "invalid_link_reference":
        case "invalid_asset_reference": return "fix_resource_reference";
        case "asset_metadata_missing": return "use_governed_asset";
        case "asset_single_byte_budget_exceeded": return "optimize_asset";
        case "asset_reference_budget_exceeded":
        case "asset_transfer_budget_exceeded": return "reduce_authored_assets";
        case "invalid_action_spec": return "fix_action_spec";
        case "unknown_action_capability": return "declare_action_capability";
        case "disallowed_action_capability": return "grant_action_capability";
        case "invalid_action_input": return "fix_action_input";
        case "deprecated_action_capability": return "replace_deprecated_action";
        case "invalid_data_spec": return "fix_data_spec";
        case "unknown_data_capability": return "declare_data_capability";
        case "disallowed_data_capability": return "grant_data_capability";
        case "invalid_data_input": return "fix_data_input";
        case "deprecated_data_capability": return "replace_deprecated_data";
        case "missing_data_output_schema": return "declare_data_output_schema";
        case "conflicting_data_source": return "choose_data_source";
        case "invalid_data_output": return "repair_data_resolver";
        case "missing_image_alternative": return "add_image_alternative";
        case "unsafe_responsive_layout": return "use_intrinsic_grid";
        case "invalid_responsive_measure": return "fix_responsive_measure";
    }
}
/**
 * Validate an untrusted Pigment document against a capability manifest.
 *
 * This is deliberately fail-closed: a malformed manifest produces only an
 * actionable manifest diagnostic, never best-effort document approval.
 */
export function validatePigmentDocument(document, manifest) {
    const manifestVersion = isRecord(manifest) && typeof manifest.version === "string" ? manifest.version : null;
    const manifestValidation = validatePigmentCapabilityManifest(manifest);
    if (!manifestValidation.ok) {
        return {
            schema: PIGMENT_DOCUMENT_VALIDATION_SCHEMA,
            ok: false,
            manifestVersion,
            diagnostics: manifestValidation.issues.map((entry) => ({
                code: "invalid_manifest",
                severity: "error",
                path: `$.manifest${entry.path === "$" ? "" : entry.path.startsWith("$") ? entry.path.slice(1) : `.${entry.path}`}`,
                message: `Capability manifest is invalid: ${entry.message}`,
                repairKind: "repair_manifest",
            })),
        };
    }
    const diagnostics = validateDocumentShape(document);
    if (diagnostics.some((entry) => entry.severity === "error")) {
        return { schema: PIGMENT_DOCUMENT_VALIDATION_SCHEMA, ok: false, manifestVersion: manifestValidation.manifest.version, diagnostics };
    }
    if (!isPigmentDocument(document)) {
        // The shape pass above should already explain this. Keep the boundary
        // closed even if an underlying document predicate gains a new condition.
        diagnostic(diagnostics, { code: "malformed_document", severity: "error", path: "$", message: "Document does not satisfy the Pigment document contract.", repairKind: "repair_document_shape" });
    }
    else {
        for (const finding of validatePigmentDocumentConformance(document, manifestValidation.manifest)) {
            diagnostic(diagnostics, { ...finding, repairKind: repairKindForConformance(finding) });
        }
    }
    return {
        schema: PIGMENT_DOCUMENT_VALIDATION_SCHEMA,
        ok: !diagnostics.some((entry) => entry.severity === "error"),
        manifestVersion: manifestValidation.manifest.version,
        diagnostics,
    };
}
export function isPigmentCapabilityManifest(value) {
    return validatePigmentCapabilityManifest(value).ok;
}
function sortStrings(values) {
    return values ? [...values].sort(compareCanonicalText) : undefined;
}
/** Locale-independent ordering for every manifest inventory that contributes to canonical bytes. */
function compareCanonicalText(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
function sortJson(value) {
    if (Array.isArray(value))
        return value.map(sortJson);
    if (isRecord(value))
        return Object.fromEntries(Object.keys(value).sort(compareCanonicalText).map((key) => [key, sortJson(value[key])]));
    return value;
}
function normalizeDeprecation(value) {
    return value && { message: value.message, ...(value.replacement ? { replacement: value.replacement } : {}) };
}
function normalizeValueSchema(value) {
    return {
        kind: value.kind,
        ...(value.description ? { description: value.description } : {}),
        ...(value.required !== undefined ? { required: value.required } : {}),
        ...(value.format ? { format: value.format } : {}),
        ...(value.enum ? { enum: [...value.enum] } : {}),
        ...(value.default !== undefined ? { default: sortJson(value.default) } : {}),
        ...(value.items ? { items: normalizeValueSchema(value.items) } : {}),
        ...(value.properties ? { properties: normalizeSchemaRecord(value.properties) } : {}),
        ...(value.capabilityBinding ? { capabilityBinding: value.capabilityBinding } : {}),
        ...(value.resourceKind ? { resourceKind: value.resourceKind } : {}),
        ...(value.deprecated ? { deprecated: normalizeDeprecation(value.deprecated) } : {}),
    };
}
function normalizeSchemaRecord(value) {
    return Object.fromEntries(Object.keys(value).sort(compareCanonicalText).map((key) => [key, normalizeValueSchema(value[key])]));
}
function normalizeResourcePolicy(value) {
    return {
        allowedSchemes: sortStrings(value.allowedSchemes) ?? [],
        ...(value.allowRelative !== undefined ? { allowRelative: value.allowRelative } : {}),
        ...(value.allowedOrigins ? { allowedOrigins: sortStrings(value.allowedOrigins) } : {}),
    };
}
function normalizeAssetResourcePolicy(value) {
    return {
        ...normalizeResourcePolicy(value),
        ...(value.requireMetadata !== undefined ? { requireMetadata: value.requireMetadata } : {}),
    };
}
function normalizeBlock(block) {
    return {
        type: block.type,
        ...(block.label ? { label: block.label } : {}),
        ...(block.description ? { description: block.description } : {}),
        ...(block.props ? { props: normalizeSchemaRecord(block.props) } : {}),
        ...(block.allowedChildren ? { allowedChildren: sortStrings(block.allowedChildren) } : {}),
        ...(block.slots ? { slots: [...block.slots].map((slot) => ({
                name: slot.name,
                ...(slot.description ? { description: slot.description } : {}),
                ...(slot.minChildren !== undefined ? { minChildren: slot.minChildren } : {}),
                ...(slot.maxChildren !== undefined ? { maxChildren: slot.maxChildren } : {}),
                ...(slot.allowedTypes ? { allowedTypes: sortStrings(slot.allowedTypes) } : {}),
            })).sort((a, b) => compareCanonicalText(a.name, b.name)) } : {}),
        ...(block.dataCapabilities ? { dataCapabilities: sortStrings(block.dataCapabilities) } : {}),
        ...(block.dataBindings ? { dataBindings: [...block.dataBindings]
                .map((binding) => ({
                property: binding.property,
                ...(binding.mutuallyExclusiveWith ? { mutuallyExclusiveWith: sortStrings(binding.mutuallyExclusiveWith) } : {}),
            }))
                .sort((a, b) => compareCanonicalText(a.property, b.property)) } : {}),
        ...(block.actionCapabilities ? { actionCapabilities: sortStrings(block.actionCapabilities) } : {}),
        ...(block.resourceBindings ? { resourceBindings: [...block.resourceBindings]
                .map((binding) => ({ property: binding.property, kind: binding.kind }))
                .sort((a, b) => compareCanonicalText(a.property, b.property)) } : {}),
        ...(block.accessibility ? { accessibility: {
                ...(block.accessibility.imageAlternative ? { imageAlternative: {
                        sourceProperty: block.accessibility.imageAlternative.sourceProperty,
                        alternativeProperty: block.accessibility.imageAlternative.alternativeProperty,
                        ...(block.accessibility.imageAlternative.allowEmptyAlternative !== undefined
                            ? { allowEmptyAlternative: block.accessibility.imageAlternative.allowEmptyAlternative }
                            : {}),
                    } } : {}),
            } } : {}),
        ...(block.responsive ? { responsive: {
                layout: block.responsive.layout,
                columnsProperty: block.responsive.columnsProperty,
                minColumnWidthProperty: block.responsive.minColumnWidthProperty,
                minColumnWidthPx: block.responsive.minColumnWidthPx,
                maxColumnWidthPx: block.responsive.maxColumnWidthPx,
                ...(block.responsive.allowedExplicitTemplates
                    ? { allowedExplicitTemplates: sortStrings(block.responsive.allowedExplicitTemplates) }
                    : {}),
            } } : {}),
        ...(block.examples ? { examples: block.examples.map((example) => sortJson(example)) } : {}),
        ...(block.deprecated ? { deprecated: normalizeDeprecation(block.deprecated) } : {}),
    };
}
/**
 * Produce a stable, JSON-serializable manifest without mutating the caller's
 * values. Arrays used as ordered page content (block trees) retain their order;
 * capability inventories are sorted by their identifier.
 */
export function normalizePigmentCapabilityManifest(manifest) {
    return {
        schema: PIGMENT_CAPABILITY_MANIFEST_SCHEMA,
        version: manifest.version,
        blocks: [...manifest.blocks].map(normalizeBlock).sort((a, b) => compareCanonicalText(a.type, b.type)),
        ...(manifest.tokens ? { tokens: [...manifest.tokens].map((token) => ({
                name: token.name,
                kind: token.kind,
                ...(token.description ? { description: token.description } : {}),
                ...(token.deprecated ? { deprecated: normalizeDeprecation(token.deprecated) } : {}),
            })).sort((a, b) => compareCanonicalText(a.name, b.name)) } : {}),
        ...(manifest.dataCapabilities ? { dataCapabilities: [...manifest.dataCapabilities].map((capability) => ({
                name: capability.name,
                ...(capability.description ? { description: capability.description } : {}),
                ...(capability.input ? { input: normalizeSchemaRecord(capability.input) } : {}),
                ...(capability.output ? { output: normalizeValueSchema(capability.output) } : {}),
                ...(capability.deprecated ? { deprecated: normalizeDeprecation(capability.deprecated) } : {}),
            })).sort((a, b) => compareCanonicalText(a.name, b.name)) } : {}),
        ...(manifest.actionCapabilities ? { actionCapabilities: [...manifest.actionCapabilities].map((capability) => ({
                name: capability.name,
                ...(capability.description ? { description: capability.description } : {}),
                ...(capability.input ? { input: normalizeSchemaRecord(capability.input) } : {}),
                ...(capability.deprecated ? { deprecated: normalizeDeprecation(capability.deprecated) } : {}),
            })).sort((a, b) => compareCanonicalText(a.name, b.name)) } : {}),
        ...(manifest.assets ? { assets: [...manifest.assets]
                .map((asset) => ({ reference: asset.reference, mediaType: asset.mediaType, byteSize: asset.byteSize, sha256: asset.sha256 }))
                .sort((a, b) => compareCanonicalText(a.reference, b.reference)) } : {}),
        ...(manifest.validation ? { validation: {
                ...(manifest.validation.links ? { links: normalizeResourcePolicy(manifest.validation.links) } : {}),
                ...(manifest.validation.assets ? { assets: normalizeAssetResourcePolicy(manifest.validation.assets) } : {}),
                ...(manifest.validation.authoredAssetBudget ? { authoredAssetBudget: {
                        ...(manifest.validation.authoredAssetBudget.maxUniqueReferences !== undefined
                            ? { maxUniqueReferences: manifest.validation.authoredAssetBudget.maxUniqueReferences }
                            : {}),
                        ...(manifest.validation.authoredAssetBudget.maxSingleAssetBytes !== undefined
                            ? { maxSingleAssetBytes: manifest.validation.authoredAssetBudget.maxSingleAssetBytes }
                            : {}),
                        ...(manifest.validation.authoredAssetBudget.maxUniqueTransferBytes !== undefined
                            ? { maxUniqueTransferBytes: manifest.validation.authoredAssetBudget.maxUniqueTransferBytes }
                            : {}),
                    } } : {}),
            } } : {}),
        ...(manifest.examples ? { examples: [...manifest.examples].map((example) => ({
                id: example.id,
                ...(example.title ? { title: example.title } : {}),
                ...(example.description ? { description: example.description } : {}),
                document: sortJson(example.document),
            })).sort((a, b) => compareCanonicalText(a.id, b.id)) } : {}),
    };
}
/** Throw a concise error when a caller wants a fail-closed manifest boundary. */
export function assertPigmentCapabilityManifest(value) {
    const validation = validatePigmentCapabilityManifest(value);
    if (validation.ok)
        return validation.manifest;
    throw new Error(`Invalid Pigment capability manifest: ${validation.issues.map((entry) => `${entry.path} (${entry.code})`).join(", ")}`);
}
